import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { IconX } from "../icons";
import { formatAnswerTimestamp } from "../../lib/dates/formatAnswerDate";
import { formatSubmissionValue } from "../../lib/forms/formatSubmissionValue";
import { getSubmissionFieldValue } from "../../lib/forms/normalizeSubmission";
import { groupSubmissionsForTable } from "../../lib/forms/submissionsTableView";
import type { FormBuilderField } from "../../types/formBuilder";
import type { FormSubmissionDto } from "../../types/submission";
import "./submissionsPrint.css";

interface SubmissionsPrintControlsProps {
  formName: string;
  submissions: FormSubmissionDto[];
  fields: FormBuilderField[];
  visibleFields: FormBuilderField[];
  cityNames: Record<string, string>;
  groupByFieldId: number | null;
  getGroupValue: (submission: FormSubmissionDto, fieldId: number) => string;
  showRow: boolean;
  showId: boolean;
  showDate: boolean;
}

function SubmissionsPrintDocument({
  formName,
  submissions,
  fields,
  visibleFields,
  cityNames,
  groupByFieldId,
  getGroupValue,
  showRow,
  showId,
  showDate,
  scale,
}: SubmissionsPrintControlsProps & { scale: number }) {
  const groups =
    groupByFieldId == null
      ? [{ key: "all", label: "", items: submissions }]
      : groupSubmissionsForTable(submissions, groupByFieldId, getGroupValue);
  const printRowColumn =
    showRow || (!showId && !showDate && visibleFields.length === 0);
  const columnCount =
    Number(printRowColumn) +
    Number(showId) +
    Number(showDate) +
    visibleFields.length;
  let rowOffset = 0;

  return (
    <div
      className="submissions-print-document"
      style={{ fontSize: `${scale / 10}pt` }}
    >
      <header className="submissions-print-header">
        <h1>{formName.trim() || "Ответы формы"}</h1>
        <p>Ответов: {submissions.length}</p>
      </header>
      <table>
        <thead>
          <tr>
            {printRowColumn ? <th scope="col">№</th> : null}
            {showId ? <th scope="col">ID ответа</th> : null}
            {showDate ? <th scope="col">Дата ответа</th> : null}
            {visibleFields.map((field) => (
              <th key={field.clientId} scope="col">
                {field.label.trim() || field.name || `Поле #${field.id}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const firstRow = rowOffset;
            rowOffset += group.items.length;
            return (
              <Fragment key={group.key}>
                {groupByFieldId != null ? (
                  <tr className="submissions-print-group">
                    <th scope="rowgroup" colSpan={columnCount}>
                      {group.label} <span>{group.items.length}</span>
                    </th>
                  </tr>
                ) : null}
                {group.items.map((submission, index) => (
                  <tr key={submission.id}>
                    {printRowColumn ? <td>{firstRow + index + 1}</td> : null}
                    {showId ? <td>{submission.id}</td> : null}
                    {showDate ? (
                      <td>
                        {formatAnswerTimestamp(
                          submission.createdAt ?? submission.created_at,
                        )}
                      </td>
                    ) : null}
                    {visibleFields.map((field) => (
                      <td key={field.clientId}>
                        {formatSubmissionValue(
                          field,
                          getSubmissionFieldValue(
                            submission.data,
                            field,
                            fields,
                          ),
                          {
                            cityNames,
                            ages: submission.ages,
                            asOf: submission.createdAt ?? submission.created_at,
                          },
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function SubmissionsPrintControls(props: SubmissionsPrintControlsProps) {
  const [scale, setScale] = useState(100);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [printReady, setPrintReady] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const clearPrint = () => {
      document.body.classList.remove("printing-submissions");
      setPrintReady(false);
      setPreviewOpen(false);
    };
    window.addEventListener("afterprint", clearPrint);
    return () => {
      window.removeEventListener("afterprint", clearPrint);
      document.body.classList.remove("printing-submissions");
    };
  }, []);

  useEffect(() => {
    if (!previewOpen) return;
    const trigger = triggerRef.current;
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPreviewOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]):not([tabindex='-1']), input:not([disabled])",
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      trigger?.focus();
    };
  }, [previewOpen]);

  const handlePrint = () => {
    document.body.classList.add("printing-submissions");
    flushSync(() => setPrintReady(true));
    try {
      window.print();
    } catch {
      document.body.classList.remove("printing-submissions");
      setPrintReady(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={props.submissions.length === 0}
        onClick={() => setPreviewOpen(true)}
        className="ml-auto rounded-md border border-slate-200 bg-white px-3 py-1.5 font-onest text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Печать
      </button>

      {previewOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="submissions-print-preview-title"
            >
              <button
                type="button"
                className="absolute inset-0 bg-slate-900/60"
                aria-hidden="true"
                tabIndex={-1}
                onClick={() => setPreviewOpen(false)}
              />
              <div
                ref={dialogRef}
                className="relative flex h-[min(94vh,60rem)] w-full max-w-[90rem] flex-col overflow-hidden rounded-lg bg-white shadow-2xl"
              >
                <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
                  <h2
                    id="submissions-print-preview-title"
                    className="font-onest text-base font-semibold text-ink"
                  >
                    Предпросмотр печати
                  </h2>
                  <button
                    ref={closeButtonRef}
                    type="button"
                    aria-label="Закрыть предпросмотр"
                    onClick={() => setPreviewOpen(false)}
                    className="rounded p-1.5 text-slate-500 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
                  >
                    <IconX size={18} />
                  </button>
                </header>
                <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
                  <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 px-4 py-3 lg:w-56 lg:flex-col lg:items-stretch lg:gap-2 lg:border-b-0 lg:border-r">
                    <label
                      htmlFor="submissions-print-scale"
                      className="font-onest text-xs font-medium text-slate-700"
                    >
                      Масштаб
                    </label>
                    <input
                      id="submissions-print-scale"
                      type="range"
                      min="75"
                      max="175"
                      step="25"
                      value={scale}
                      onChange={(event) => setScale(Number(event.target.value))}
                      className="min-w-0 flex-1 accent-brand-600"
                    />
                    <output
                      htmlFor="submissions-print-scale"
                      className="w-10 text-right font-onest text-xs tabular-nums text-slate-700 lg:w-auto lg:text-left"
                    >
                      {scale}%
                    </output>
                  </div>
                  <div className="min-h-0 flex-1 overflow-auto bg-slate-100 p-3 sm:p-6">
                    <div className="submissions-print-preview-page">
                      <SubmissionsPrintDocument {...props} scale={scale} />
                    </div>
                  </div>
                </div>
                <footer className="flex shrink-0 justify-end gap-2 border-t border-slate-200 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setPreviewOpen(false)}
                    className="rounded-md px-3 py-2 font-onest text-sm text-slate-600 hover:bg-slate-100"
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="rounded-md bg-brand-600 px-4 py-2 font-onest text-sm font-medium text-white hover:bg-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
                  >
                    Печать
                  </button>
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}

      {printReady
        ? createPortal(
            <>
              <style>{"@page { size: A4 landscape; margin: 10mm; }"}</style>
              <main className="submissions-print-sheet">
                <SubmissionsPrintDocument {...props} scale={scale} />
              </main>
            </>,
            document.body,
          )
        : null}
    </>
  );
}
